import React, { useState } from "react";
import clsx from "clsx";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import "../../common/form-input/form-input.component.css";
import { ReactComponent as VisibilityIcon } from "../../../assets/visibility-icon.svg";
import { ReactComponent as VisibilityOffIcon } from "../../../assets/visibility-off-icon.svg";
import { ReactComponent as CheckmarkIcon } from "../../../assets/checkmark.svg";
import { validateEmail } from "../../../utils/validate-email.util";
import { useRegisterForm } from "./useRegisterForm";
import { RegisterFormValues } from "./RegisterForm.types";
import { useStyles } from "./RegisterForm.styles";

interface Props {
  handleClose: () => void;
}

const RegisterForm: React.FC<Props> = ({ handleClose }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const [showPassword, setShowPassword] = useState(false);

  const { onSubmit, registrationComplete } = useRegisterForm();

  const {
    register,
    handleSubmit,
    watch,
    formState: { isValid, isSubmitting },
  } = useForm<RegisterFormValues>({ mode: "onChange" });

  const emailValue = watch("email");
  const passwordValue = watch("password");

  if (registrationComplete) {
    return (
      <div className={classes.successContainer}>
        <div className={classes.successIconWrapper}>
          <CheckmarkIcon className={classes.successIcon} />
        </div>
        <span className={classes.successMessage}>
          {t("auth.registrationSuccessMessage")}
        </span>
        <button
          type="button"
          className={classes.button}
          onClick={handleClose}
        >
          {t("common.close")}
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={classes.form}>
      <fieldset disabled={isSubmitting} className={classes.fieldset}>
        <div className="group">
          <input
            id="register-email"
            type="text"
            className={clsx("form-input", "h6-regular", classes.fieldFont, classes.fieldInputText)}
            {...register("email", {
              required: true,
              validate: (value) => validateEmail(value),
            })}
          />
          <label
            htmlFor="register-email"
            className={clsx("h6-regular", "form-input-label", classes.fieldFont, {
              shrink: !!emailValue,
            })}
          >
            {t("auth.Email")}
          </label>
        </div>

        <div className={clsx("group", classes.passwordGroup)}>
          <input
            id="register-password"
            type={showPassword ? "text" : "password"}
            className={clsx("form-input", "h6-regular", classes.fieldFont, classes.fieldInputText)}
            {...register("password", { required: true })}
          />
          <label
            htmlFor="register-password"
            className={clsx("h6-regular", "form-input-label", classes.fieldFont, {
              shrink: !!passwordValue,
            })}
          >
            {t("auth.Password")}
          </label>
          <span
            className={clsx(classes.passwordToggle, {
              [classes.disabledLink]: isSubmitting,
            })}
            onClick={() => {
              if (isSubmitting) return;
              setShowPassword((prev) => !prev);
            }}
          >
            {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
          </span>
        </div>

        <div className={classes.spacer} />

        <button
          type="submit"
          className={classes.button}
          disabled={isSubmitting || !isValid}
        >
          {isSubmitting ? t("auth.registering") : t("auth.Register")}
        </button>
      </fieldset>
    </form>
  );
};

export default RegisterForm;
